import { useEffect, useMemo, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import * as THREE from "three";
import { ghostTimeDelta, sampleGhost, type GhostRecording } from "../lib/ghostReplay";

const GHOST_COLOR = "#38f0ff";

function useGhostModel(): THREE.Object3D | null {
  const [model, setModel] = useState<THREE.Object3D | null>(null);
  useEffect(() => {
    let cancelled = false;
    new GLTFLoader().load(
      "./f16.glb",
      (gltf) => {
        if (cancelled) return;
        const clone = gltf.scene.clone(true);
        const box = new THREE.Box3().setFromObject(clone);
        const size = new THREE.Vector3();
        const center = new THREE.Vector3();
        box.getSize(size);
        box.getCenter(center);
        const maxDim = Math.max(size.x, size.y, size.z) || 1;
        const scale = 0.12 / maxDim;
        clone.scale.setScalar(scale);
        clone.position.set(-center.x * scale, -center.y * scale, -center.z * scale);
        const ghostMat = new THREE.MeshBasicMaterial({
          color: GHOST_COLOR,
          transparent: true,
          opacity: 0.34,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
          side: THREE.DoubleSide,
        });
        clone.traverse((child) => {
          const mesh = child as THREE.Mesh;
          if (mesh.isMesh) mesh.material = ghostMat;
        });
        setModel(clone);
      },
      undefined,
      () => {
        // Fall back to the primitive cone rendered below.
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);
  return model;
}

/**
 * Renders a translucent ghost of the pilot's best run, advancing its own clock
 * in real time whenever `active` (so it freezes on pause) and reports a live
 * time delta versus the live player back to the HUD.
 */
export function GhostJet({
  recording,
  active,
  runId,
  playerRef,
  onDelta,
}: {
  recording: GhostRecording;
  active: boolean;
  runId: number;
  playerRef: React.MutableRefObject<THREE.Vector3>;
  onDelta: (deltaSeconds: number | null) => void;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const clock = useRef(0);
  const deltaTimer = useRef(0);
  const euler = useMemo(() => new THREE.Euler(0, 0, 0, "YXZ"), []);
  const quat = useMemo(() => new THREE.Quaternion(), []);
  const model = useGhostModel();

  // Restart playback whenever a new run begins.
  useEffect(() => {
    clock.current = 0;
    deltaTimer.current = 0;
    const first = recording.frames[0];
    if (groupRef.current && first) {
      groupRef.current.position.set(first.x, first.y, first.z);
    }
    onDelta(null);
  }, [runId, recording, onDelta]);

  useFrame((_, delta) => {
    if (!active) return;
    clock.current += delta;
    const pose = sampleGhost(recording.frames, clock.current);
    if (pose && groupRef.current) {
      groupRef.current.position.set(pose.position[0], pose.position[1], pose.position[2]);
      euler.set(pose.pitch, pose.yaw, 0);
      quat.setFromEuler(euler);
      groupRef.current.quaternion.copy(quat);
      groupRef.current.visible = true;
    }
    deltaTimer.current += delta;
    if (deltaTimer.current >= 0.2) {
      deltaTimer.current = 0;
      const p = playerRef.current;
      onDelta(ghostTimeDelta(recording.frames, { x: p.x, y: p.y, z: p.z }, clock.current));
    }
  });

  return (
    <group ref={groupRef}>
      {model ? (
        <primitive object={model} />
      ) : (
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <coneGeometry args={[0.035, 0.14, 6]} />
          <meshBasicMaterial color={GHOST_COLOR} transparent opacity={0.4} />
        </mesh>
      )}
      <mesh position={[0, -0.09, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.07, 0.09, 24]} />
        <meshBasicMaterial color={GHOST_COLOR} transparent opacity={0.5} side={THREE.DoubleSide} />
      </mesh>
      <Html center distanceFactor={9} position={[0, 0.16, 0]} className="ghost-jet-label">
        GHOST
      </Html>
    </group>
  );
}
